/**
 * Pixel art documents (`*.pixel.json`, `prism.pixel/1`) and the drawing
 * algorithms of the Pixel Art tab. Pure functions; no DOM.
 *
 * In memory every cell (frame × layer) is a straight-alpha RGBA buffer.
 * On disk a cell is `null` (empty), readable palette rows (one character per
 * pixel: `0-9A-Za-z` index the palette, `.` is transparent) or
 * `{ "rgbaz": "<base64 zlib RGBA>" }` for large or many-colored art (painted
 * textures); `{ "rgba": "<base64>" }` is read too. The engine loads these
 * documents directly as sprite sheets, tilesets and model textures.
 */

import { unzlibSync, zlibSync } from 'fflate';

export const PIXEL_FORMAT = 'prism.pixel/1';
export const PALETTE_CHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
export const MAX_PIXEL_SIZE = 4096;
/** Cells up to this many pixels are saved as readable palette rows. */
export const ROWS_MAX_PIXELS = 128 * 128;

export type RGBA = [number, number, number, number];

export interface PixelLayer {
  name: string;
  visible: boolean;
  opacity: number;
}

export interface PixelAnimation {
  frames: number[];
  fps: number;
  loop: boolean;
}

export interface PixelDoc {
  width: number;
  height: number;
  palette: string[];
  layers: PixelLayer[];
  /** `cells[frame][layer]`, each `width * height * 4` bytes. */
  cells: Uint8ClampedArray[][];
  animations: Record<string, PixelAnimation>;
  pivot?: [number, number];
  /** Model texture filtering (default: nearest up to 256 px, else linear). */
  filter?: 'nearest' | 'linear';
}

/** A small default palette (Sweetie 16 + white). */
export const DEFAULT_PALETTE = [
  '#1a1c2c', '#5d275d', '#b13e53', '#ef7d57', '#ffcd75', '#a7f070', '#38b764', '#257179',
  '#29366f', '#3b5dc9', '#41a6f6', '#73eff7', '#f4f4f4', '#94b0c2', '#566c86', '#333c57',
];

export function emptyCell(doc: Pick<PixelDoc, 'width' | 'height'>): Uint8ClampedArray {
  return new Uint8ClampedArray(doc.width * doc.height * 4);
}

export function newPixelDoc(width = 16, height = 16): PixelDoc {
  const doc: PixelDoc = {
    width,
    height,
    palette: [...DEFAULT_PALETTE],
    layers: [{ name: 'Layer 1', visible: true, opacity: 1 }],
    cells: [],
    animations: {},
  };
  doc.cells = [[emptyCell(doc)]];
  return doc;
}

// ------------------------------------------------------------------ colors

export function parseHex(hex: string): RGBA | null {
  const h = hex.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(h))
    return [parseInt(h[0]! + h[0]!, 16), parseInt(h[1]! + h[1]!, 16), parseInt(h[2]! + h[2]!, 16), 255];
  if (/^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(h))
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
      h.length === 8 ? parseInt(h.slice(6, 8), 16) : 255,
    ];
  return null;
}

export function toHex(c: RGBA): string {
  const p = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${p(c[0])}${p(c[1])}${p(c[2])}${c[3] === 255 ? '' : p(c[3])}`;
}

export function sameColor(a: RGBA, b: RGBA): boolean {
  if (a[3] === 0 && b[3] === 0) return true;
  return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

// ------------------------------------------------------------------ base64

export function bytesToBase64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function base64ToBytes(text: string): Uint8Array {
  const s = atob(text.replace(/\s+/g, ''));
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

// ------------------------------------------------------------------ file format

function decodeCell(doc: PixelDoc, raw: unknown, where: string): Uint8ClampedArray {
  const { width: w, height: h } = doc;
  if (raw === null || raw === undefined) return emptyCell(doc);
  if (Array.isArray(raw)) {
    if (raw.length !== h) throw new Error(`${where}: has ${raw.length} rows, expected ${h}`);
    const pal = doc.palette.map((c) => parseHex(c) ?? ([0, 0, 0, 0] as RGBA));
    const out = emptyCell(doc);
    raw.forEach((row, y) => {
      if (typeof row !== 'string' || row.length !== w)
        throw new Error(`${where}: row ${y} must be a string of ${w} characters`);
      for (let x = 0; x < w; x++) {
        const ch = row[x]!;
        if (ch === '.') continue;
        const i = PALETTE_CHARS.indexOf(ch);
        const c = pal[i];
        if (i < 0 || !c) throw new Error(`${where}: row ${y} uses '${ch}', which is not in the palette`);
        out.set(c, (y * w + x) * 4);
      }
    });
    return out;
  }
  if (typeof raw === 'object' && typeof (raw as { rgbaz?: unknown }).rgbaz === 'string') {
    let bytes: Uint8Array;
    try {
      bytes = unzlibSync(base64ToBytes((raw as { rgbaz: string }).rgbaz));
    } catch {
      throw new Error(`${where}: rgbaz is not valid zlib data`);
    }
    if (bytes.length !== w * h * 4) throw new Error(`${where}: rgbaz has ${bytes.length} bytes, expected ${w * h * 4}`);
    return new Uint8ClampedArray(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }
  if (typeof raw === 'object' && typeof (raw as { rgba?: unknown }).rgba === 'string') {
    const bytes = base64ToBytes((raw as { rgba: string }).rgba);
    if (bytes.length !== w * h * 4) throw new Error(`${where}: rgba has ${bytes.length} bytes, expected ${w * h * 4}`);
    return new Uint8ClampedArray(bytes);
  }
  throw new Error(`${where}: a cell must be null, an array of rows or { "rgba": ... }`);
}

export function parsePixelDoc(text: string): PixelDoc {
  const raw = JSON.parse(text) as Record<string, unknown>;
  if (raw.format !== PIXEL_FORMAT) throw new Error(`format must be "${PIXEL_FORMAT}"`);
  const width = Number(raw.width ?? 16);
  const height = Number(raw.height ?? 16);
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width > MAX_PIXEL_SIZE || height > MAX_PIXEL_SIZE)
    throw new Error(`width and height must be whole numbers from 1 to ${MAX_PIXEL_SIZE}`);
  const palette = Array.isArray(raw.palette) ? raw.palette.map(String) : [];
  const layersRaw = Array.isArray(raw.layers) && raw.layers.length ? raw.layers : [{}];
  const layers: PixelLayer[] = layersRaw.map((l: Record<string, unknown>, i) => ({
    name: typeof l.name === 'string' ? l.name : `Layer ${i + 1}`,
    visible: l.visible !== false,
    opacity: typeof l.opacity === 'number' ? Math.min(1, Math.max(0, l.opacity)) : 1,
  }));
  const doc: PixelDoc = { width, height, palette, layers, cells: [], animations: {} };
  const framesRaw = Array.isArray(raw.frames) && raw.frames.length ? raw.frames : [{}];
  doc.cells = framesRaw.map((f: { cells?: unknown[] }, fi) =>
    layers.map((_, li) => decodeCell(doc, f?.cells?.[li], `frames[${fi}].cells[${li}]`)),
  );
  const anims = (raw.animations ?? {}) as Record<string, Partial<PixelAnimation>>;
  for (const [name, a] of Object.entries(anims)) {
    doc.animations[name] = {
      frames: Array.isArray(a.frames) ? a.frames.map(Number).filter((n) => Number.isInteger(n) && n >= 0) : [],
      fps: typeof a.fps === 'number' && a.fps > 0 ? a.fps : 8,
      loop: a.loop !== false,
    };
  }
  if (Array.isArray(raw.pivot) && raw.pivot.length === 2) doc.pivot = [Number(raw.pivot[0]), Number(raw.pivot[1])];
  if (raw.filter === 'nearest' || raw.filter === 'linear') doc.filter = raw.filter;
  return doc;
}

function encodeCell(doc: PixelDoc, cell: Uint8ClampedArray, lookup: Map<string, number>): unknown {
  let empty = true;
  for (let i = 3; i < cell.length; i += 4) if (cell[i] !== 0) { empty = false; break; }
  if (empty) return null;
  const packed = () => ({ rgbaz: bytesToBase64(zlibSync(new Uint8Array(cell.buffer, cell.byteOffset, cell.byteLength), { level: 6 })) });
  if (doc.width * doc.height > ROWS_MAX_PIXELS) return packed();
  const rows: string[] = [];
  for (let y = 0; y < doc.height; y++) {
    let row = '';
    for (let x = 0; x < doc.width; x++) {
      const o = (y * doc.width + x) * 4;
      if (cell[o + 3] === 0) { row += '.'; continue; }
      const idx = lookup.get(toHex([cell[o]!, cell[o + 1]!, cell[o + 2]!, cell[o + 3]!]));
      if (idx === undefined || idx >= PALETTE_CHARS.length) return packed();
      row += PALETTE_CHARS[idx];
    }
    rows.push(row);
  }
  return rows;
}

export function serializePixelDoc(doc: PixelDoc): string {
  const lookup = new Map<string, number>();
  doc.palette.forEach((c, i) => {
    const rgba = parseHex(c);
    if (rgba && !lookup.has(toHex(rgba))) lookup.set(toHex(rgba), i);
  });
  const out: Record<string, unknown> = {
    format: PIXEL_FORMAT,
    width: doc.width,
    height: doc.height,
    palette: doc.palette,
    layers: doc.layers,
    frames: doc.cells.map((layers) => ({ cells: layers.map((c) => encodeCell(doc, c, lookup)) })),
    animations: doc.animations,
  };
  if (doc.pivot) out.pivot = doc.pivot;
  if (doc.filter) out.filter = doc.filter;
  return `${JSON.stringify(out, null, 2)}\n`;
}

// ------------------------------------------------------------------ pixels

export function getPixel(doc: Pick<PixelDoc, 'width' | 'height'>, cell: Uint8ClampedArray, x: number, y: number): RGBA {
  if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return [0, 0, 0, 0];
  const o = (y * doc.width + x) * 4;
  return [cell[o]!, cell[o + 1]!, cell[o + 2]!, cell[o + 3]!];
}

export function setPixel(doc: Pick<PixelDoc, 'width' | 'height'>, cell: Uint8ClampedArray, x: number, y: number, c: RGBA): void {
  if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return;
  cell.set(c, (y * doc.width + x) * 4);
}

/** Bresenham line points from (x0, y0) to (x1, y1), inclusive. */
export function linePoints(x0: number, y0: number, x1: number, y1: number): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  let x = x0;
  let y = y0;
  for (;;) {
    pts.push([x, y]);
    if (x === x1 && y === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x += sx; }
    if (e2 <= dx) { err += dx; y += sy; }
  }
  return pts;
}

export function rectPoints(x0: number, y0: number, x1: number, y1: number, filled: boolean): Array<[number, number]> {
  const [ax, bx] = [Math.min(x0, x1), Math.max(x0, x1)];
  const [ay, by] = [Math.min(y0, y1), Math.max(y0, y1)];
  const pts: Array<[number, number]> = [];
  for (let y = ay; y <= by; y++)
    for (let x = ax; x <= bx; x++)
      if (filled || x === ax || x === bx || y === ay || y === by) pts.push([x, y]);
  return pts;
}

/** Pixel ellipse inscribed in the box (x0, y0)-(x1, y1). */
export function ellipsePoints(x0: number, y0: number, x1: number, y1: number, filled: boolean): Array<[number, number]> {
  const [ax, bx] = [Math.min(x0, x1), Math.max(x0, x1)];
  const [ay, by] = [Math.min(y0, y1), Math.max(y0, y1)];
  const cx = (ax + bx) / 2;
  const cy = (ay + by) / 2;
  const rx = (bx - ax) / 2 + 0.5;
  const ry = (by - ay) / 2 + 0.5;
  const inside = (x: number, y: number) => ((x - cx) / rx) ** 2 + ((y - cy) / ry) ** 2 <= 1.0;
  const pts: Array<[number, number]> = [];
  for (let y = ay; y <= by; y++)
    for (let x = ax; x <= bx; x++) {
      if (!inside(x, y)) continue;
      if (filled || !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1)) pts.push([x, y]);
    }
  return pts;
}

/** Points to paint for a flood fill at (x, y): contiguous (4-way) or every pixel of that color. */
export function fillPoints(doc: Pick<PixelDoc, 'width' | 'height'>, cell: Uint8ClampedArray, x: number, y: number, contiguous = true): Array<[number, number]> {
  if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return [];
  const target = getPixel(doc, cell, x, y);
  const pts: Array<[number, number]> = [];
  if (!contiguous) {
    for (let py = 0; py < doc.height; py++)
      for (let px = 0; px < doc.width; px++) if (sameColor(getPixel(doc, cell, px, py), target)) pts.push([px, py]);
    return pts;
  }
  const seen = new Uint8Array(doc.width * doc.height);
  const stack: Array<[number, number]> = [[x, y]];
  while (stack.length) {
    const [px, py] = stack.pop()!;
    if (px < 0 || py < 0 || px >= doc.width || py >= doc.height) continue;
    const i = py * doc.width + px;
    if (seen[i]) continue;
    seen[i] = 1;
    if (!sameColor(getPixel(doc, cell, px, py), target)) continue;
    pts.push([px, py]);
    stack.push([px + 1, py], [px - 1, py], [px, py + 1], [px, py - 1]);
  }
  return pts;
}

/** Expands points for a square brush and mirror axes. */
export function brushPoints(
  doc: Pick<PixelDoc, 'width' | 'height'>,
  pts: Array<[number, number]>,
  size: number,
  mirrorX: boolean,
  mirrorY: boolean,
): Array<[number, number]> {
  const out = new Map<number, [number, number]>();
  const r0 = -Math.floor((size - 1) / 2);
  const add = (x: number, y: number) => {
    if (x >= 0 && y >= 0 && x < doc.width && y < doc.height) out.set(y * doc.width + x, [x, y]);
  };
  for (const [x, y] of pts)
    for (let dy = 0; dy < size; dy++)
      for (let dx = 0; dx < size; dx++) {
        const px = x + r0 + dx;
        const py = y + r0 + dy;
        add(px, py);
        if (mirrorX) add(doc.width - 1 - px, py);
        if (mirrorY) add(px, doc.height - 1 - py);
        if (mirrorX && mirrorY) add(doc.width - 1 - px, doc.height - 1 - py);
      }
  return [...out.values()];
}

/** Straight-alpha "over" of `src` (with opacity) onto `dst`, in place. */
export function blendOver(dst: Uint8ClampedArray, src: Uint8ClampedArray, opacity = 1): void {
  for (let i = 0; i < dst.length; i += 4) {
    const sa = (src[i + 3]! / 255) * opacity;
    if (sa <= 0) continue;
    const da = dst[i + 3]! / 255;
    const oa = sa + da * (1 - sa);
    for (let c = 0; c < 3; c++) dst[i + c] = (src[i + c]! * sa + dst[i + c]! * da * (1 - sa)) / oa;
    dst[i + 3] = oa * 255;
  }
}

/** One frame with all visible layers composited. */
export function compositeFrame(doc: PixelDoc, frame: number): Uint8ClampedArray {
  const out = emptyCell(doc);
  const cells = doc.cells[frame];
  if (!cells) return out;
  doc.layers.forEach((layer, i) => {
    if (layer.visible && layer.opacity > 0 && cells[i]) blendOver(out, cells[i]!, layer.opacity);
  });
  return out;
}

/** Columns of the exported / engine sheet (matches prism_core::pixel). */
export function sheetColumns(doc: PixelDoc): number {
  return Math.min(Math.max(1, doc.cells.length), Math.max(1, Math.floor(4096 / doc.width)));
}

/** All frames row by row (what the engine draws), for PNG export. */
export function composeSheet(doc: PixelDoc): { pixels: Uint8ClampedArray; width: number; height: number } {
  const cols = sheetColumns(doc);
  const rows = Math.ceil(doc.cells.length / cols);
  const width = doc.width * cols;
  const height = doc.height * rows;
  const pixels = new Uint8ClampedArray(width * height * 4);
  doc.cells.forEach((_, f) => {
    const px = compositeFrame(doc, f);
    const ox = (f % cols) * doc.width;
    const oy = Math.floor(f / cols) * doc.height;
    for (let y = 0; y < doc.height; y++)
      pixels.set(px.subarray(y * doc.width * 4, (y + 1) * doc.width * 4), ((oy + y) * width + ox) * 4);
  });
  return { pixels, width, height };
}

/** Copies a cell into a canvas of a new size (anchored top-left, or centered). */
export function resizeCell(
  cell: Uint8ClampedArray,
  from: { width: number; height: number },
  to: { width: number; height: number },
  anchor: 'top-left' | 'center' | 'bottom' = 'top-left',
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(to.width * to.height * 4);
  const ox = anchor === 'top-left' ? 0 : Math.floor((to.width - from.width) / 2);
  const oy = anchor === 'top-left' ? 0 : anchor === 'center' ? Math.floor((to.height - from.height) / 2) : to.height - from.height;
  for (let y = 0; y < from.height; y++)
    for (let x = 0; x < from.width; x++) {
      const tx = x + ox;
      const ty = y + oy;
      if (tx < 0 || ty < 0 || tx >= to.width || ty >= to.height) continue;
      out.set(cell.subarray((y * from.width + x) * 4, (y * from.width + x) * 4 + 4), (ty * to.width + tx) * 4);
    }
  return out;
}

export function flipCell(doc: Pick<PixelDoc, 'width' | 'height'>, cell: Uint8ClampedArray, horizontal: boolean): Uint8ClampedArray {
  const out = new Uint8ClampedArray(cell.length);
  for (let y = 0; y < doc.height; y++)
    for (let x = 0; x < doc.width; x++) {
      const sx = horizontal ? doc.width - 1 - x : x;
      const sy = horizontal ? y : doc.height - 1 - y;
      out.set(cell.subarray((sy * doc.width + sx) * 4, (sy * doc.width + sx) * 4 + 4), (y * doc.width + x) * 4);
    }
  return out;
}

/** Shifts a cell by whole pixels (wrapping), e.g. for the move tool. */
export function shiftCell(doc: Pick<PixelDoc, 'width' | 'height'>, cell: Uint8ClampedArray, dx: number, dy: number, wrap = false): Uint8ClampedArray {
  const out = new Uint8ClampedArray(cell.length);
  for (let y = 0; y < doc.height; y++)
    for (let x = 0; x < doc.width; x++) {
      let tx = x + dx;
      let ty = y + dy;
      if (wrap) {
        tx = ((tx % doc.width) + doc.width) % doc.width;
        ty = ((ty % doc.height) + doc.height) % doc.height;
      } else if (tx < 0 || ty < 0 || tx >= doc.width || ty >= doc.height) continue;
      out.set(cell.subarray((y * doc.width + x) * 4, (y * doc.width + x) * 4 + 4), (ty * doc.width + tx) * 4);
    }
  return out;
}

/** Slices a sprite sheet image into frames of `fw` × `fh` (row by row). */
export function sliceSheet(pixels: Uint8ClampedArray, width: number, height: number, fw: number, fh: number): Uint8ClampedArray[] {
  const cols = Math.max(1, Math.floor(width / fw));
  const rows = Math.max(1, Math.floor(height / fh));
  const frames: Uint8ClampedArray[] = [];
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const out = new Uint8ClampedArray(fw * fh * 4);
      for (let y = 0; y < fh; y++) {
        const sy = r * fh + y;
        if (sy >= height) break;
        const start = (sy * width + c * fw) * 4;
        out.set(pixels.subarray(start, start + Math.min(fw, width - c * fw) * 4), y * fw * 4);
      }
      frames.push(out);
    }
  return frames;
}

/** Distinct opaque colors used across the document (for "palette from art"). */
export function usedColors(doc: PixelDoc, limit = PALETTE_CHARS.length): string[] {
  const seen = new Set<string>();
  for (const frame of doc.cells)
    for (const cell of frame)
      for (let i = 0; i < cell.length; i += 4) {
        if (cell[i + 3] === 0) continue;
        seen.add(toHex([cell[i]!, cell[i + 1]!, cell[i + 2]!, cell[i + 3]!]));
        if (seen.size >= limit) return [...seen];
      }
  return [...seen];
}
